import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatDateTime } from '../../../lib/formatDate';
import {
  errorMessage, toastError, toastInfo, toastSuccess,
} from '../../../lib/toast';
import { adminOdooCheck, adminSyncNow, adminSyncStatus } from '../../vendors/vendorsApi';
import { VENDOR_ERRORS } from '../../vendors/vendorLabels';
import '../../vendors/Vendors.css';

const POLL_MS = 2000;
// Same ceiling as the org-chart refresh: a run that never reports finished
// must not poll for as long as the admin tab stays open.
const MAX_POLL_ATTEMPTS = 150; // 150 * 2s = 5 minutes.

function summaryText(s) {
  if (!s) return '';
  const base = `${s.tags} tags, ${s.vendors} vendors, ${s.flagged} flagged as changed outside Nexus`;
  return s.baseline ? `${base} (first run: baseline, nothing flagged)` : base;
}

function SyncStatus({ status, failed }) {
  // A failed read is an error, never "no sync has run": unknown is not none.
  if (failed) return <p className="vd-inline-error" role="alert">Couldn&apos;t load the sync status.</p>;
  if (!status) return <p className="vd-sync-line vd-muted" aria-busy="true">Checking the last sync…</p>;
  if (!status.last_run_at) return <p className="vd-sync-line">No sync has run since the server started.</p>;
  return (
    <dl className="vd-sync-facts">
      <dt>Last run</dt>
      <dd>{formatDateTime(status.last_run_at)}</dd>
      {status.last_summary && (
        <>
          <dt>Result</dt>
          <dd>{summaryText(status.last_summary)}</dd>
        </>
      )}
    </dl>
  );
}

function CheckRow({ label, ok }) {
  return (
    <li className="vd-check-row">
      <span>{label}</span>
      <span className={`vd-chip ${ok ? 'vd-chip--ok' : 'vd-chip--bad'}`}>{ok ? 'Ready' : 'Missing'}</span>
    </li>
  );
}

function OdooCheck({ check }) {
  if (check.state === 'loading') return <p className="vd-sync-line vd-muted" aria-busy="true">Checking Odoo…</p>;
  const iso = check.data?.iso;
  // Unknown is not "ready" and not "missing".
  if (check.state === 'error' || !iso) return <p className="vd-inline-error" role="alert">Couldn&apos;t reach Odoo to check the fields.</p>;
  const missing = iso.missing || [];
  return (
    <>
      <ul className="vd-check-list">
        <CheckRow label="ISO fields" ok={!!iso.ready} />
        <CheckRow label="Vendor review activity type" ok={!!check.data.activity_type} />
      </ul>
      {!iso.ready && missing.length > 0 && (
        <p className="vd-check-note">Add these fields in Odoo: {missing.join(', ')}</p>
      )}
    </>
  );
}

/** Last vendor sync, a Sync now button that polls to completion, and the Odoo fields check. */
export default function VendorSyncPanel() {
  const [status, setStatus] = useState(null);
  const [statusFailed, setStatusFailed] = useState(false);
  const [running, setRunning] = useState(false);
  const [check, setCheck] = useState({ state: 'loading', data: null });

  const mounted = useRef(true);
  const pollRef = useRef(null);
  const attempts = useRef(0);
  const lastRunBefore = useRef(null);

  const readStatus = useCallback(async () => {
    try {
      const s = await adminSyncStatus();
      if (!mounted.current) return null;
      setStatus(s);
      setStatusFailed(false);
      return s;
    } catch {
      if (mounted.current) setStatusFailed(true);
      return null;
    }
  }, []);

  const poll = useCallback(() => {
    pollRef.current = setTimeout(async () => {
      attempts.current += 1;
      try {
        const s = await adminSyncStatus();
        if (!mounted.current) return;
        if (s.running) {
          if (attempts.current >= MAX_POLL_ATTEMPTS) {
            setRunning(false);
            toastInfo('The sync is taking longer than expected.');
            return;
          }
          poll();
          return;
        }
        setStatus(s);
        setRunning(false);
        // The server records a run only when it succeeded. A finished run that
        // left the last-run time untouched failed, so never call it success.
        if (s.last_run_at && s.last_run_at !== lastRunBefore.current) toastSuccess('Vendor sync finished.');
        else toastError("The sync didn't finish. Check the Odoo connection and try again.");
      } catch {
        if (!mounted.current) return;
        setRunning(false);
        toastError('Could not confirm the sync finished.');
      }
    }, POLL_MS);
  }, []);

  useEffect(() => {
    mounted.current = true;
    (async () => {
      const s = await readStatus();
      // Someone else's run is in flight: show it and follow it.
      if (s?.running && mounted.current) {
        lastRunBefore.current = s.last_run_at;
        setRunning(true);
        attempts.current = 0;
        poll();
      }
    })();
    adminOdooCheck()
      .then((d) => { if (mounted.current) setCheck({ state: 'ready', data: d }); })
      .catch(() => { if (mounted.current) setCheck({ state: 'error', data: null }); });
    return () => {
      mounted.current = false;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [readStatus, poll]);

  const syncNow = async () => {
    setRunning(true);
    attempts.current = 0;
    lastRunBefore.current = status?.last_run_at ?? null;
    try {
      await adminSyncNow();
      if (!mounted.current) return;
      poll();
    } catch (err) {
      if (!mounted.current) return;
      if (err?.response?.status === 409) {
        toastInfo('A sync is already running.');
        poll();
        return;
      }
      setRunning(false);
      toastError(errorMessage(err, 'Could not start the sync.', VENDOR_ERRORS));
    }
  };

  return (
    <>
      <section className="vd-panel" aria-labelledby="vd-adm-sync">
        <div className="vd-panel-head">
          <h2 id="vd-adm-sync" className="vd-panel-title">Vendor sync</h2>
        </div>
        <p className="vd-panel-lede">Pulls vendors and their tags from Odoo into Nexus.</p>
        <SyncStatus status={status} failed={statusFailed} />
        <button type="button" className="btn btn-primary vd-sync-btn" onClick={syncNow} disabled={running} aria-busy={running || undefined}>
          {running ? 'Syncing…' : 'Sync now'}
        </button>
      </section>

      <section className="vd-panel" aria-labelledby="vd-adm-check">
        <div className="vd-panel-head">
          <h2 id="vd-adm-check" className="vd-panel-title">Odoo fields check</h2>
        </div>
        <OdooCheck check={check} />
      </section>
    </>
  );
}
